export type InputNumberValue = string | number | null | undefined;

type Options = Intl.NumberFormatOptions | undefined;

const DEFAULT_LOCALE = { code: 'pt-BR', currency: 'BRL' };

function processInput(inputValue: InputNumberValue): number | null {
  if (inputValue == null || Number.isNaN(inputValue)) return null;

  return Number(inputValue);
}

function ensureTwoDecimalPlaces(value: string): string {
  if (!value.includes(',')) {
    return value + ',00';
  }

  const [, decimalPart] = value.split(',');

  if (decimalPart?.length === 1) {
    return value + '0';
  }

  return value;
}
// ----------------------------------------------------------------------

export function fNumber(inputValue: InputNumberValue, options?: Options) {
  const locale = DEFAULT_LOCALE;

  const number = processInput(inputValue);

  if (number === null) return '';

  return new Intl.NumberFormat(locale.code, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(number);
}

// ----------------------------------------------------------------------

export function fCurrency(inputValue: InputNumberValue, options?: Options) {
  const locale = DEFAULT_LOCALE;

  const number = processInput(inputValue);

  if (number === null) return '';

  const result = new Intl.NumberFormat(locale.code, {
    style: 'currency',
    currency: locale.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return ensureTwoDecimalPlaces(result);
}

export function fCurrencySemRS(
  inputValue: InputNumberValue,
  options?: Options
) {
  const locale = DEFAULT_LOCALE;

  const number = processInput(inputValue);

  if (number === null) return '';

  const value = new Intl.NumberFormat(locale.code, {
    style: 'currency',
    currency: locale.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  const result = ensureTwoDecimalPlaces(value);

  return result.replace('R$', '');
}
// ----------------------------------------------------------------------

export function fPercent(inputValue: InputNumberValue, options?: Options) {
  const locale = DEFAULT_LOCALE;

  const number = processInput(inputValue);

  if (number === null) return '';

  return new Intl.NumberFormat(locale.code, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...options,
  }).format(number / 100);
}

// ----------------------------------------------------------------------

export function fShortenNumber(
  inputValue: InputNumberValue,
  options?: Options
) {
  const locale = DEFAULT_LOCALE;

  const number = processInput(inputValue);

  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    notation: 'compact',
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return fm.replace(/[A-Z]/g, (match) => match.toLowerCase());
}

// ----------------------------------------------------------------------

export function fData(inputValue: InputNumberValue) {
  const number = processInput(inputValue);

  if (number === null || number === 0) return '0 bytes';

  const units = ['bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];
  const decimal = 2;
  const baseValue = 1024;

  const index = Math.floor(Math.log(number) / Math.log(baseValue));

  return `${parseFloat((number / baseValue ** index).toFixed(decimal))} ${units[index]}`;
}

export function parseCurrency(value: string): number {
  if (!value) return 0;

  const sanitized = value
    .replace(/\s/g, '')
    .replace('R$', '')
    .replace(/\./g, '')
    .replace(',', '.');

  const number = parseFloat(sanitized);

  return isNaN(number) ? 0 : number;
}
